<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { CreateOrUpdateSemesterRequest, SemesterListItem } from '@/types'

import { appConfirm } from '@/composables/useAppConfirm'
import { appTableSortableHeader } from '@/composables/useAppTable'
import { useAppToast } from '@/composables/useAppToast'
import * as semesterService from '@/services/semesterService'
import { formatLocalDateOnly } from '@/utils/dateOnly'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const { d, t, locale } = useI18n()
const toast = useAppToast()

// State
const semesters = ref<SemesterListItem[]>([])
const isLoading = ref(false)
const error = ref<string | null>(null)
const showDialog = ref(false)
const isEditing = ref(false)
const isSaving = ref(false)

// Form state
const formData = ref<CreateOrUpdateSemesterRequest>({
  id: 0,
  startDate: '',
  endDate: '',
  lectureStart: undefined,
  lectureEnd: undefined,
  isPublished: false,
  showAsDefaultFromDate: undefined,
  campusTermGuid: '',
})
const editingId = ref<number | null>(null)

// Computed
const dialogTitle = computed(() =>
  isEditing.value
    ? t('pages.admin.semesters.dialog.titleEdit')
    : t('pages.admin.semesters.dialog.titleCreate'),
)
const sortedSemesters = computed(() => [...semesters.value].sort((a, b) => b.id - a.id))
const columns = computed<TableColumn<SemesterListItem>[]>(() => [
  {
    accessorKey: 'id',
    header: appTableSortableHeader<SemesterListItem>(t('pages.admin.semesters.fields.id')),
    meta: { class: { th: 'w-[8%]' } },
  },
  {
    accessorKey: 'nameEN',
    header: appTableSortableHeader<SemesterListItem>(t('pages.admin.semesters.fields.name')),
    meta: { class: { th: 'w-[17%]' } },
  },
  {
    accessorKey: 'startDate',
    header: appTableSortableHeader<SemesterListItem>(t('pages.admin.semesters.fields.startDate')),
    meta: { class: { th: 'w-[15%]' } },
  },
  {
    accessorKey: 'endDate',
    header: appTableSortableHeader<SemesterListItem>(t('pages.admin.semesters.fields.endDate')),
    meta: { class: { th: 'w-[15%]' } },
  },
  {
    id: 'lecturePeriod',
    header: t('pages.admin.semesters.fields.lecturePeriod'),
    meta: { class: { th: 'w-[15%]' } },
  },
  {
    accessorKey: 'isPublished',
    header: t('pages.admin.semesters.fields.status'),
    meta: { class: { th: 'w-[12%]' } },
  },
  {
    accessorKey: 'campusTermGuid',
    header: t('pages.admin.semesters.fields.campusGuid'),
    meta: { class: { th: 'w-[15%]' } },
  },
  {
    id: 'actions',
    header: t('pages.admin.semesters.fields.actions'),
    meta: { class: { th: 'w-[11%]' } },
  },
])

// Helper to get locale-aware semester name
const getSemesterName = (semester: SemesterListItem) => {
  return locale.value === 'de' ? semester.nameDE : semester.nameEN
}

const getSemesterFullName = (semester: SemesterListItem) => {
  return locale.value === 'de' ? semester.fullNameDE : semester.fullNameEN
}

function formatLectureDate(value?: string | null, fallback?: string) {
  const date = parseDateOnly(value ?? fallback)
  return date ? d(date, 'short') : '…'
}

function formatLecturePeriod(semester: SemesterListItem) {
  return `${formatLectureDate(semester.lectureStart)} – ${formatLectureDate(semester.lectureEnd)}`
}

function getSemesterRowId(semester: SemesterListItem) {
  return String(semester.id)
}

function parseDateOnly(value?: string) {
  if (!value) {
    return undefined
  }

  const [datePart] = value.split('T')
  if (!datePart) {
    return undefined
  }

  const [year, month, day] = datePart.split('-').map(Number)
  if (!year || !month || !day) {
    return undefined
  }

  return new Date(year, month - 1, day)
}

function toUtcMidnightIsoString(value?: string) {
  if (!value) {
    return undefined
  }

  return `${value}T00:00:00.000Z`
}

// Methods
async function loadSemesters() {
  isLoading.value = true
  error.value = null

  try {
    const response = await semesterService.getAllSemesters()
    semesters.value = response.semesters
  } catch (err) {
    console.error('Error loading semesters:', err)
    error.value = err instanceof Error ? err.message : t('pages.admin.semesters.messages.loadError')
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.admin.semesters.messages.loadError'),
      duration: 3000,
    })
  } finally {
    isLoading.value = false
  }
}

function openCreateDialog() {
  isEditing.value = false
  editingId.value = null

  // Calculate next semester ID (e.g., if latest is 20252, next could be 20261)
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  // Summer semester (1) starts around April, Winter semester (2) starts around October
  const semesterType = currentMonth >= 4 && currentMonth < 10 ? 1 : 2
  const nextId = currentYear * 10 + semesterType

  formData.value = {
    id: nextId,
    startDate: '',
    endDate: '',
    lectureStart: undefined,
    lectureEnd: undefined,
    isPublished: false,
    showAsDefaultFromDate: undefined,
    campusTermGuid: '',
  }
  showDialog.value = true
}

function openEditDialog(semester: SemesterListItem) {
  isEditing.value = true
  editingId.value = semester.id
  formData.value = {
    id: semester.id,
    startDate: formatLocalDateOnly(parseDateOnly(semester.startDate)),
    endDate: formatLocalDateOnly(parseDateOnly(semester.endDate)),
    lectureStart:
      formatLocalDateOnly(parseDateOnly(semester.lectureStart ?? undefined)) || undefined,
    lectureEnd: formatLocalDateOnly(parseDateOnly(semester.lectureEnd ?? undefined)) || undefined,
    isPublished: semester.isPublished,
    showAsDefaultFromDate:
      formatLocalDateOnly(parseDateOnly(semester.showAsDefaultFromDate)) || undefined,
    campusTermGuid: semester.campusTermGuid,
  }
  showDialog.value = true
}

// Computed properties for DatePicker components (convert between Date and string)
const startDateModel = computed({
  get: () => parseDateOnly(formData.value.startDate),
  set: (value: Date | undefined) => {
    formData.value.startDate = formatLocalDateOnly(value)
  },
})

const endDateModel = computed({
  get: () => parseDateOnly(formData.value.endDate),
  set: (value: Date | undefined) => {
    formData.value.endDate = formatLocalDateOnly(value)
  },
})

const lectureStartModel = computed({
  get: () => parseDateOnly(formData.value.lectureStart),
  set: (value: Date | undefined) => {
    formData.value.lectureStart = formatLocalDateOnly(value) || undefined
  },
})

const lectureEndModel = computed({
  get: () => parseDateOnly(formData.value.lectureEnd),
  set: (value: Date | undefined) => {
    formData.value.lectureEnd = formatLocalDateOnly(value) || undefined
  },
})

const lecturePeriodInvalid = computed(
  () =>
    !!formData.value.lectureStart &&
    !!formData.value.lectureEnd &&
    formData.value.lectureEnd <= formData.value.lectureStart,
)

const defaultFromDateModel = computed({
  get: () => parseDateOnly(formData.value.showAsDefaultFromDate),
  set: (value: Date | undefined) => {
    formData.value.showAsDefaultFromDate = formatLocalDateOnly(value) || undefined
  },
})

function closeDialog() {
  showDialog.value = false
  formData.value = {
    id: 0,
    startDate: '',
    endDate: '',
    lectureStart: undefined,
    lectureEnd: undefined,
    isPublished: false,
    showAsDefaultFromDate: undefined,
    campusTermGuid: '',
  }
  editingId.value = null
}

async function saveSemester() {
  isSaving.value = true

  try {
    // Keep these date-only fields pinned to midnight UTC for the API payload.
    const requestData: CreateOrUpdateSemesterRequest = {
      ...formData.value,
      startDate: toUtcMidnightIsoString(formData.value.startDate) ?? '',
      endDate: toUtcMidnightIsoString(formData.value.endDate) ?? '',
      lectureStart: toUtcMidnightIsoString(formData.value.lectureStart),
      lectureEnd: toUtcMidnightIsoString(formData.value.lectureEnd),
      showAsDefaultFromDate: toUtcMidnightIsoString(formData.value.showAsDefaultFromDate),
    }

    if (isEditing.value && editingId.value !== null) {
      await semesterService.updateSemester(editingId.value, requestData)
      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.admin.semesters.messages.updateSuccess'),
        duration: 3000,
      })
    } else {
      await semesterService.createSemester(requestData)
      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.admin.semesters.messages.createSuccess'),
        duration: 3000,
      })
    }

    await loadSemesters()
    closeDialog()
  } catch (err) {
    console.error('Error saving semester:', err)
    const errorMessage =
      err instanceof Error ? err.message : t('pages.admin.semesters.messages.saveError')
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: errorMessage,
      duration: 5000,
    })
  } finally {
    isSaving.value = false
  }
}

async function confirmDelete(semester: SemesterListItem) {
  const accepted = await appConfirm({
    message: t('pages.admin.semesters.dialog.confirmDelete', { name: getSemesterName(semester) }),
    title: t('pages.admin.semesters.dialog.confirmDeleteHeader'),
    cancelLabel: t('pages.admin.semesters.actions.cancel'),
    confirmLabel: t('pages.admin.semesters.actions.delete'),
    confirmColor: 'error',
  })
  if (accepted) await deleteSemester(semester.id)
}

async function deleteSemester(id: number) {
  try {
    await semesterService.deleteSemester(id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.admin.semesters.messages.deleteSuccess'),
      duration: 3000,
    })
    await loadSemesters()
  } catch (err) {
    console.error('Error deleting semester:', err)
    const errorMessage =
      err instanceof Error ? err.message : t('pages.admin.semesters.messages.saveError')
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: errorMessage,
      duration: 5000,
    })
  }
}

// Lifecycle
onMounted(() => {
  loadSemesters()
})
</script>

<template>
  <div class="admin-semesters-page">
    <!-- Page Header -->
    <div class="mb-6 flex items-start justify-between">
      <div>
        <h1 class="text-3xl font-bold text-neutral-900 dark:text-white">
          {{ t('pages.admin.semesters.title') }}
        </h1>
        <p class="mt-2 text-muted">
          {{ t('pages.admin.semesters.subtitle') }}
        </p>
      </div>
      <UButton
        icon="i-lucide-plus"
        :label="t('pages.admin.semesters.actions.create')"
        @click="openCreateDialog"
      />
    </div>

    <!-- Error Message -->
    <AppAlert v-if="error" color="error" :closable="true" class="mb-4" @close="error = null">
      {{ error }}
    </AppAlert>

    <!-- Data Table -->
    <AppDataTableShell
      :data="sortedSemesters"
      :columns="columns"
      :loading="isLoading"
      :get-row-id="getSemesterRowId"
    >
      <template #empty>
        <div class="py-8 text-center text-muted">
          {{ t('pages.admin.semesters.empty') }}
        </div>
      </template>

      <template #id-cell="{ row }">
        <span class="font-mono font-semibold text-[var(--app-text-primary)]">{{
          row.original.id
        }}</span>
      </template>

      <template #nameEN-cell="{ row }">
        <div>
          <div class="font-semibold">
            {{ getSemesterName(row.original) }}
          </div>
          <div class="text-sm text-muted">
            {{ getSemesterFullName(row.original) }}
          </div>
        </div>
      </template>

      <template #startDate-cell="{ row }">
        {{ d(parseDateOnly(row.original.startDate) ?? new Date(row.original.startDate), 'short') }}
      </template>

      <template #endDate-cell="{ row }">
        {{ d(parseDateOnly(row.original.endDate) ?? new Date(row.original.endDate), 'short') }}
      </template>

      <template #lecturePeriod-cell="{ row }">
        <span v-if="row.original.lectureStart || row.original.lectureEnd">
          {{ formatLecturePeriod(row.original) }}
        </span>
        <span v-else class="text-muted">–</span>
      </template>

      <template #isPublished-cell="{ row }">
        <div class="flex flex-col gap-1">
          <UBadge
            v-if="row.original.isPublished"
            color="success"
            :label="t('pages.admin.semesters.status.published')"
          />
          <UBadge
            v-if="row.original.isDefault"
            color="info"
            :label="t('pages.admin.semesters.status.default')"
          />
        </div>
      </template>

      <template #campusTermGuid-cell="{ row }">
        <span class="font-mono text-xs">{{ row.original.campusTermGuid }}</span>
      </template>

      <template #actions-cell="{ row }">
        <div class="flex gap-2">
          <UButton
            v-app-tooltip.top="t('pages.admin.semesters.actions.edit')"
            icon="i-lucide-pencil"
            color="neutral"
            @click="openEditDialog(row.original)"
            variant="ghost"
          />
          <UButton
            v-app-tooltip.top="t('pages.admin.semesters.actions.delete')"
            icon="i-lucide-trash"
            color="error"
            @click="confirmDelete(row.original)"
            variant="ghost"
          />
        </div>
      </template>
    </AppDataTableShell>

    <!-- Create/Edit Dialog -->
    <AppModal
      v-model:open="showDialog"
      :title="dialogTitle"
      :modal="true"
      :closable="!isSaving"
      :close-on-escape="!isSaving"
      class="w-full max-w-2xl"
    >
      <div class="space-y-4">
        <!-- Semester ID -->
        <div class="flex flex-col gap-2">
          <label for="semesterId" class="font-semibold">{{
            t('pages.admin.semesters.fields.semesterId')
          }}</label>
          <UInputNumber
            id="semesterId"
            v-model="formData.id"
            :disabled="isSaving || isEditing"
            :min="20000"
            :max="29999"
            class="w-full"
            :increment="false"
            :decrement="false"
            :format-options="{ useGrouping: false }"
          />
          <small class="text-muted">{{ t('pages.admin.semesters.fields.semesterIdHelp') }}</small>
        </div>

        <!-- Start Date -->
        <div class="flex flex-col gap-2">
          <label for="startDate" class="font-semibold">{{
            t('pages.admin.semesters.fields.startDate')
          }}</label>
          <AppDatePicker
            id="startDate"
            v-model="startDateModel"
            :disabled="isSaving"
            show-icon
            icon-display="input"
            fluid
          />
        </div>

        <!-- End Date -->
        <div class="flex flex-col gap-2">
          <label for="endDate" class="font-semibold">{{
            t('pages.admin.semesters.fields.endDate')
          }}</label>
          <AppDatePicker
            id="endDate"
            v-model="endDateModel"
            :disabled="isSaving"
            show-icon
            icon-display="input"
            fluid
          />
        </div>

        <!-- Lecture period -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="flex flex-col gap-2">
            <label for="lectureStart" class="font-semibold">{{
              t('pages.admin.semesters.fields.lectureStart')
            }}</label>
            <AppDatePicker
              id="lectureStart"
              v-model="lectureStartModel"
              :disabled="isSaving"
              :show-clear="true"
              show-icon
              icon-display="input"
              fluid
            />
          </div>
          <div class="flex flex-col gap-2">
            <label for="lectureEnd" class="font-semibold">{{
              t('pages.admin.semesters.fields.lectureEnd')
            }}</label>
            <AppDatePicker
              id="lectureEnd"
              v-model="lectureEndModel"
              :disabled="isSaving"
              :show-clear="true"
              show-icon
              icon-display="input"
              fluid
            />
          </div>
          <small class="text-muted sm:col-span-2">{{
            t('pages.admin.semesters.fields.lecturePeriodHelp')
          }}</small>
        </div>

        <!-- Campus Term GUID -->
        <div class="flex flex-col gap-2">
          <label for="campusTermGuid" class="font-semibold">{{
            t('pages.admin.semesters.fields.campusTermGuid')
          }}</label>
          <UInput
            id="campusTermGuid"
            v-model="formData.campusTermGuid"
            :placeholder="t('pages.admin.semesters.fields.campusTermGuidPlaceholder')"
            :disabled="isSaving"
            class="w-full"
          />
          <small class="text-muted">{{
            t('pages.admin.semesters.fields.campusTermGuidHelp')
          }}</small>
        </div>

        <!-- Is Active -->
        <div class="flex items-center gap-3">
          <UCheckbox id="isPublished" v-model="formData.isPublished" :disabled="isSaving" />
          <label for="isPublished" class="cursor-pointer font-semibold">{{
            t('pages.admin.semesters.fields.isPublished')
          }}</label>
        </div>

        <!-- Show As Default From Date -->
        <div class="flex flex-col gap-2">
          <label for="showAsDefaultFromDate" class="font-semibold">{{
            t('pages.admin.semesters.fields.showAsDefaultFrom')
          }}</label>
          <AppDatePicker
            id="showAsDefaultFromDate"
            v-model="defaultFromDateModel"
            :disabled="isSaving"
            :show-clear="true"
            show-icon
            icon-display="input"
            fluid
          />
          <small class="text-muted">{{
            t('pages.admin.semesters.fields.showAsDefaultFromHelp')
          }}</small>
        </div>
      </div>

      <template #footer>
        <UButton
          :label="t('pages.admin.semesters.actions.cancel')"
          color="neutral"
          :disabled="isSaving"
          @click="closeDialog"
          variant="outline"
        />
        <UButton
          :label="isEditing ? t('common.actions.update') : t('common.actions.create')"
          :loading="isSaving"
          :disabled="
            !formData.id ||
            !formData.startDate ||
            !formData.endDate ||
            !formData.campusTermGuid ||
            lecturePeriodInvalid
          "
          @click="saveSemester"
        />
      </template>
    </AppModal>
  </div>
</template>

<style scoped>
.admin-semesters-page {
  padding: 1.5rem;
}
</style>
